import { useMutation } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { bulkEditItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import {
  BulkEditPayload,
  Category,
  CourierRestriction,
  MenuContainer,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { ConfirmModal } from 'common/components';
import { reportBulkAction } from 'modules/menu/behavioral';
import { useBulkChangeDeliveryTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  modalType: 'disableBikeDelivery' | 'disableCarDelivery' | 'disableMotorcycleDelivery';
  onClose: () => void;
  onClearSelection: () => void;
  selectedIds: Set<string>;
};

const modalTypeToTitleMap: Record<Props['modalType'], string> = {
  disableBikeDelivery: 'menu.bulkActions.courierRestrictions.disableBikeDelivery',
  disableCarDelivery: 'menu.bulkActions.courierRestrictions.disableCarDelivery',
  disableMotorcycleDelivery: 'menu.bulkActions.courierRestrictions.disableMotorcycleDelivery',
};

const modalTypeToCourierRestrictionMap: Record<Props['modalType'], CourierRestriction> = {
  disableBikeDelivery: 'no_bikes',
  disableCarDelivery: 'no_cars',
  disableMotorcycleDelivery: 'no_motorcycles',
};

const StyledConfirmModal = styled(ConfirmModal)`
  width: 480px;
`;

const BulkCourierRestrictionModal = ({
  categories,
  menuId,
  modalType,
  onClose,
  onClearSelection,
  selectedIds,
}: Props) => {
  const { updateMenuData } = useMenuData();
  const trackDeliveryMethodChange = useBulkChangeDeliveryTracking([...selectedIds]);
  const handleSubmit = useMutation(
    () => {
      const payload: BulkEditPayload = {
        ...buildBulkActionBasePayload(categories, selectedIds),
        add_courier_restriction: modalTypeToCourierRestrictionMap[modalType],
      };
      return bulkEditItems(menuId, payload);
    },
    {
      onMutate: () => {
        reportBulkAction(selectedIds.size, 'BULK_SET_COURIER_RESTRICTION', { courierRestrictionType: modalType });
      },
      onSuccess: (data: MenuContainer) => {
        updateMenuData({ actionType: 'updateMenuContainer', payload: data });
        onClearSelection();
        trackDeliveryMethodChange(modalType);
        toast.success(<FormattedMessage id="menu.bulkActions.courierRestrictions.successDescription" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="menu.bulkActions.courierRestrictions.errorDescription" />, {
          duration: Infinity,
        });
      },
      onSettled: () => {
        onClose();
      },
    },
  );

  return (
    <StyledConfirmModal
      cancelText={<FormattedMessage id="state.cancel" />}
      confirmText={<FormattedMessage id="state.save" />}
      description={<FormattedMessage id="menu.bulkActions.courierRestrictions.confirmDescription" />}
      header={
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id={modalTypeToTitleMap[modalType]} />}
        />
      }
      loading={handleSubmit.isLoading}
      onCancel={onClose}
      onConfirm={() => {
        handleSubmit.mutate();
      }}
      open
    />
  );
};

export default BulkCourierRestrictionModal;
